import * as React from "react";
import { Icon, cn } from "@nexus/ui";

const ADVANCED_PANEL_ID = "create-set-advanced-panel";

export interface CreateSetAdvancedSection {
  label: string;
  hint: string;
  open: boolean;
  onToggle: () => void;
  content: React.ReactNode;
}

export interface CreateSetFormLayoutProps {
  fields: React.ReactNode;
  advanced?: CreateSetAdvancedSection;
  footer: React.ReactNode;
}

/**
 * One scrolling body with Advanced options as its last row, expanding in place, so
 * the dialog never nests a bordered card inside itself. The footer stays pinned.
 */
export function CreateSetFormLayout({ fields, advanced, footer }: CreateSetFormLayoutProps) {
  return (
    <>
      <div className="-mx-5 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-5 py-5">
        {fields}
        {advanced ? <AdvancedDisclosure {...advanced} /> : null}
      </div>
      {footer}
    </>
  );
}

function AdvancedDisclosure({ label, hint, open, onToggle, content }: CreateSetAdvancedSection) {
  return (
    <section className="flex flex-col gap-4 border-t border-border pt-3">
      <button
        type="button"
        data-testid="persona-advanced-options"
        aria-expanded={open}
        aria-controls={ADVANCED_PANEL_ID}
        onClick={onToggle}
        className={cn(
          "-mx-2 flex min-h-11 items-center gap-3 rounded-md px-2 text-left transition-colors",
          "hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
        )}
      >
        <Icon name="sliders-horizontal" size="sm" color="muted" className="shrink-0" />
        <span className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-2">
          <span className="text-sm font-medium text-foreground">{label}</span>
          <span className="text-xs text-muted-foreground">{hint}</span>
        </span>
        <Icon
          name="chevron-down"
          size="xs"
          color="muted"
          className={cn(
            "shrink-0 transition-transform duration-200 motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </button>
      <div id={ADVANCED_PANEL_ID} role="region" aria-label={label} hidden={!open} className="flex flex-col gap-4">
        {content}
      </div>
    </section>
  );
}
